/**
 * The CBD Hut — predictive search intent loader
 *
 * Keeps the full header predictive-search controller off the initial main
 * thread. Existing <cbh-predictive-search> elements are automatically
 * upgraded when the controller is defined. Pointer, keyboard and touch
 * intent all trigger the load; native submitted search remains available
 * even if the enhancement cannot load.
 */
(() => {
  'use strict';

  if (window.__CBH_PREDICTIVE_SEARCH_LOADER__) return;
  window.__CBH_PREDICTIVE_SEARCH_LOADER__ = true;

  const loader = document.querySelector('script[data-cbh-predictive-controller-src]');
  const controllerSrc = loader?.dataset.cbhPredictiveControllerSrc;
  if (!controllerSrc || customElements.get('cbh-predictive-search')) return;

  let controllerPromise = null;
  const eventOptions = { capture: true, passive: true };

  function getRoot(target) {
    return target instanceof Element ? target.closest('cbh-predictive-search') : null;
  }

  function replayFocusedSearch(root) {
    const active = document.activeElement;
    if (!(active instanceof HTMLInputElement) || !root?.contains(active) || active.type !== 'search') return;

    queueMicrotask(() => {
      active.dispatchEvent(new Event('focus'));
    });
  }

  function detachIntentListeners() {
    document.removeEventListener('pointerover', onIntent, eventOptions);
    document.removeEventListener('touchstart', onIntent, eventOptions);
    document.removeEventListener('focusin', onIntent, true);
  }

  function loadController(root) {
    if (customElements.get('cbh-predictive-search')) {
      replayFocusedSearch(root);
      return Promise.resolve();
    }

    if (!controllerPromise) {
      controllerPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = controllerSrc;
        script.async = true;
        script.dataset.cbhPredictiveController = 'true';
        script.onload = () => {
          detachIntentListeners();
          resolve();
        };
        script.onerror = () => {
          script.remove();
          controllerPromise = null;
          reject(new Error('Predictive search controller failed to load'));
        };
        document.head.appendChild(script);
      });
    }

    controllerPromise.then(() => replayFocusedSearch(root)).catch(() => {});
    return controllerPromise;
  }

  function onIntent(event) {
    const root = getRoot(event.target);
    if (!root) return;
    loadController(root);
  }

  document.addEventListener('pointerover', onIntent, eventOptions);
  document.addEventListener('touchstart', onIntent, eventOptions);
  document.addEventListener('focusin', onIntent, true);

  const activeRoot = getRoot(document.activeElement);
  if (activeRoot) loadController(activeRoot);
})();
